import React, { ChangeEvent, CompositionEvent, HTMLAttributes, ReactNode, useEffect, useState } from "react";
import { styleForWidth, Width } from "../element/Width";

export function InputField<A extends HTMLAttributes<any>, T>(props: InputFieldProps<A, T> & ValueProps<T> & ChildrenProps) {
    const {
        width,
        leading,
        trailing,
        get,
        set,
        onChange: givenOnChange,
        onCompositionStart: givenOnCompositionStart,
        onCompositionEnd: givenOnCompositionEnd,
        encode,
        decode,
        transform: givenTransform,
        givenChildren,
        error,
        ...rest
    } = props;
    const fieldStyle = styleForWidth(width ?? "auto");
    const transform = givenTransform ?? (value => value);

    const errorClassName = error? "danger" : "";

    const [value, setValue] = useState("");
    const [composing, setComposing] = useState(false);

    const onChange = (event: ChangeEvent<HTMLInputElement>) => {
        givenOnChange?.(event);
        const value = transform(event.target.value);
        setValue(value);
        if (!composing) set?.(decode(value));
    };

    const onCompositionStart = (event: CompositionEvent<HTMLInputElement>) => {
        givenOnCompositionStart?.(event);
        setComposing(true);
    };

    const onCompositionEnd = (event: CompositionEvent<HTMLInputElement>) => {
        givenOnCompositionEnd?.(event);
        const value = transform(event.currentTarget.value);
        setValue(value);
        set?.(decode(value));
        setComposing(false);
    };

    useEffect(() => {
        setValue(get === undefined ? "" : encode(get));
    }, [get, encode]);

    return <span className={"input_field__field"} style={fieldStyle}>
        <span className={`input_field__focus ${errorClassName}`}>
            {leading && <span className={"input_field__leading"}>{leading}</span>}
            {givenChildren?.({ className: "input_field__input", onChange, onCompositionStart, onCompositionEnd, value, ...rest })}
            {trailing && <span className={"input_field__trailing"}>{trailing}</span>}
        </span>
    </span>;
}

type Props<T> = {
    width?: Width,
    leading?: ReactNode,
    trailing?: ReactNode,
    get?: T,
    set?: (value: T) => void,
    error?: boolean,
};

export type InputFieldProps<A extends HTMLAttributes<any>, T> = A & Props<T>;

type ValueProps<T> = {
    encode: (value: T) => string,
    decode: (value: string) => T,
    transform?: (value: string) => string,
};

type ChildrenProps = {
    givenChildren?: (props: HTMLAttributes<any>) => React.JSX.Element,
};
